import { useEffect, useState } from "react";
import { Percent, Plus, Trash2, Loader2, Save, Info } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";

// Jul-2026 (User-Wunsch · 341) — Rabatt-Limitierung auf den verhandelten
// Endpreis: Standard max. 5 %, Regeln je Marke/Modell/Betrag/Standzeit.
export default function DiscountLimitSection() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/dealer/discount-limit-config")
            .then((r) => setCfg(r.data?.config || { enabled: true, default_max_pct: 5, rules: [] }))
            .catch(() => setCfg({ enabled: true, default_max_pct: 5, rules: [] }));
    }, []);

    const upd = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
    const updRule = (idx, k, v) => setCfg((p) => ({ ...p, rules: p.rules.map((r, i) => i === idx ? { ...r, [k]: v } : r) }));
    const addRule = () => setCfg((p) => ({ ...p, rules: [...(p.rules || []), { name: "", make: "", model: "", min_price: "", max_price: "", min_stock_days: "", max_pct: 5 }] }));
    const rmRule = (idx) => setCfg((p) => ({ ...p, rules: p.rules.filter((_, i) => i !== idx) }));

    const save = async () => {
        setSaving(true);
        try {
            const r = await api.put("/dealer/discount-limit-config", { config: cfg });
            setCfg(r.data?.config || cfg);
            toast.success("Rabatt-Limits gespeichert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally { setSaving(false); }
    };

    if (!cfg) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-6 flex items-center gap-2 text-sm text-gray-500" data-testid="discount-limit-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Rabatt-Limits laden …
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6 space-y-4" data-testid="discount-limit-section">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-display text-lg font-bold flex items-center gap-2">
                        <Percent className="w-5 h-5 text-orange-500" /> Rabatt-Limits (Verkauf)
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5 max-w-xl">
                        Maximal erlaubter Rabatt auf den „Verhandelten Endpreis" in der Disposition.
                        Gilt für Mitarbeiter ohne Leitungsfunktion — Inhaber, Filial- und Verkaufsleiter
                        dürfen überschreiten.
                    </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                    <Switch checked={Boolean(cfg.enabled)} onCheckedChange={(v) => upd("enabled", v)} data-testid="discount-limit-enabled" />
                    <span className="text-xs font-bold">{cfg.enabled ? "Aktiv" : "Deaktiviert"}</span>
                </label>
            </div>

            <div className="flex items-end gap-3 flex-wrap">
                <div>
                    <Label className="text-xs font-bold">Standard-Maximalrabatt (%)</Label>
                    <Input type="number" min="0" max="100" step="0.5" value={cfg.default_max_pct}
                        onChange={(e) => upd("default_max_pct", e.target.value)}
                        className="mt-1 w-40 h-9 font-mono-num" data-testid="discount-limit-default-pct" />
                </div>
                <div className="text-[11px] text-gray-500 flex items-center gap-1.5 pb-2">
                    <Info className="w-3.5 h-3.5 shrink-0" /> Gilt, wenn keine Regel greift (Standard: 5 %).
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-600">
                        Regeln (Marke · Modell · Betrag · Standzeit)
                    </Label>
                    <Button type="button" size="sm" variant="outline" onClick={addRule} className="h-8 text-xs border-2" data-testid="discount-limit-add-rule">
                        <Plus className="w-3.5 h-3.5 mr-1" /> Regel hinzufügen
                    </Button>
                </div>
                {(cfg.rules || []).length === 0 && (
                    <div className="text-[11px] text-gray-400 border border-dashed border-gray-200 rounded-sm p-3" data-testid="discount-limit-no-rules">
                        Keine Regeln — überall gilt der Standard-Maximalrabatt. Beispiel-Regel: BMW ab 120 Tagen Standzeit → max. 10 %.
                    </div>
                )}
                <div className="space-y-2">
                    {(cfg.rules || []).map((r, idx) => (
                        <div key={r.id || idx} className="border border-gray-200 rounded-sm p-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 items-end bg-gray-50" data-testid={`discount-limit-rule-${idx}`}>
                            <div className="col-span-2">
                                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Name</label>
                                <Input value={r.name} onChange={(e) => updRule(idx, "name", e.target.value)} placeholder="z. B. Ladenhüter BMW" className="h-8 text-xs" data-testid={`discount-rule-name-${idx}`} />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Marke</label>
                                <Input value={r.make} onChange={(e) => updRule(idx, "make", e.target.value)} placeholder="alle" className="h-8 text-xs" data-testid={`discount-rule-make-${idx}`} />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Modell</label>
                                <Input value={r.model} onChange={(e) => updRule(idx, "model", e.target.value)} placeholder="alle" className="h-8 text-xs" data-testid={`discount-rule-model-${idx}`} />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Preis ab €</label>
                                <Input type="number" min="0" value={r.min_price ?? ""} onChange={(e) => updRule(idx, "min_price", e.target.value)} className="h-8 text-xs font-mono-num" data-testid={`discount-rule-minprice-${idx}`} />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Preis bis €</label>
                                <Input type="number" min="0" value={r.max_price ?? ""} onChange={(e) => updRule(idx, "max_price", e.target.value)} className="h-8 text-xs font-mono-num" data-testid={`discount-rule-maxprice-${idx}`} />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Standzeit ab (Tage)</label>
                                <Input type="number" min="0" value={r.min_stock_days ?? ""} onChange={(e) => updRule(idx, "min_stock_days", e.target.value)} className="h-8 text-xs font-mono-num" data-testid={`discount-rule-days-${idx}`} />
                            </div>
                            <div className="flex items-end gap-1.5">
                                <div className="flex-1">
                                    <label className="text-[10px] uppercase tracking-wider text-orange-700 font-bold">Max. %</label>
                                    <Input type="number" min="0" max="100" step="0.5" value={r.no_discount ? 0 : r.max_pct} disabled={!!r.no_discount} onChange={(e) => updRule(idx, "max_pct", e.target.value)} className="h-8 text-xs font-mono-num font-bold disabled:opacity-50" data-testid={`discount-rule-pct-${idx}`} />
                                </div>
                                <button type="button" onClick={() => rmRule(idx)} className="p-1.5 text-red-500 hover:text-red-700" title="Regel löschen" data-testid={`discount-rule-remove-${idx}`}>
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                            {/* Jul-2026 (User-Wunsch) — Kennzeichnung: jeglichen Rabatt ausschließen */}
                            <div className="col-span-2 sm:col-span-4 lg:col-span-8">
                                <label className="flex items-start gap-1.5 cursor-pointer select-none">
                                    <input type="checkbox" checked={!!r.no_discount} onChange={(e) => updRule(idx, "no_discount", e.target.checked)} className="mt-0.5 accent-red-600" data-testid={`discount-rule-nodiscount-${idx}`} />
                                    <span className={`text-[11px] leading-snug ${r.no_discount ? "text-red-700 font-bold" : "text-gray-600"}`}>
                                        Jeglichen Rabatt ausschließen — für passende Fahrzeuge ist <strong>kein Rabatt</strong> auf den
                                        „Verhandelten Endpreis" erlaubt (0 %). Überschreitung nur mit Freigabe / Leitungsfunktion.
                                    </span>
                                </label>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex justify-end">
                <Button type="button" onClick={save} disabled={saving} className="bg-[#111827] hover:bg-black text-[#FFCC00] font-bold" data-testid="discount-limit-save">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                    Rabatt-Limits speichern
                </Button>
            </div>
        </div>
    );
}
